/**
 * ============================================================
 * useQueries.ts — Hooks de Fetch Paginado (Deals e Clients)
 * ============================================================
 *
 * Responsabilidade: Fornecer hooks React Query para buscar deals
 *   e clients do Supabase com paginacao automatica, garantindo
 *   que todos os registros sejam carregados mesmo acima de 1000.
 *
 * Exportacoes:
 *   - useDeals(projectId): busca todos os leads/deals do projeto
 *   - useClients(projectId): busca todos os clientes do projeto
 *
 * Comportamentos-chave:
 *   - Paginacao via .range(): busca em lotes de 1000 ate esgotar
 *   - Limite de seguranca: maximo 50 paginas (50k registros)
 *   - StaleTime de 5 minutos para reduzir queries desnecessarias
 *   - Mapeamento snake_case (DB) -> camelCase (frontend)
 *
 * Quando usar: Como fonte principal de dados de deals/clients.
 *   O resultado alimenta a store Zustand via setDeals().
 * ============================================================
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Deal } from '@/store/types';
import { dbLeadToDeal } from '@/modules/crm/hooks/useSupabaseData';
import { scoreAllDeals } from '@/lib/scoring';
import { queryKeys } from '@/lib/queryKeys';

/** Tamanho do lote de paginacao — 1000 registros por request */
const PAGE_SIZE = 1000;
/** Limite de seguranca: maximo 50 paginas (50k registros) */
const MAX_PAGES = 50;

const LEAD_LIST_COLUMNS = 'id, company, contact_name, email, phone, value, mrr, cash_collect, contract_value, column_id, priority, score, origin, service, tags, notes, sdr, closer, sdr_id, closer_id, porte, client_category, territory, city, state, days_in_column, contact_logs, custom_fields, status_history, timeline, proposal_attached, proposal_attachments, contact_avatar, company_logo, company_info, contact_info, responsible, created_at, updated_at, deleted_at, utm_source, utm_medium, utm_content, utm_term, additional_contacts, last_status_date, last_contact_at';

/** Busca todos os deals de um projeto com paginacao automatica */
const fetchDeals = async (projectId: string) => {
  if (!projectId) return [];

  // Paginated fetch — accumulate all pages to keep virtual scroll working
  const allRows: any[] = [];
  let from = 0;
  let hasMore = true;
  let pages = 0;

  // `lost_reason` chegou na migration 20260914100004. Se o front subir antes
  // da migration, o PostgREST responde 42703 (coluna inexistente) — em vez de
  // derrubar a tela inteira, refaz a query sem a coluna.
  let extraCols = ', lost_reason';
  while (hasMore && pages < MAX_PAGES) {
    pages++;
    const runPage = () => supabase
      .from('leads')
      .select(LEAD_LIST_COLUMNS + extraCols)
      .eq('project_id', projectId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .range(from, from + PAGE_SIZE - 1);
    let { data, error } = await runPage();
    if (error && extraCols && (error.code === '42703' || /lost_reason/.test(error.message || ''))) {
      extraCols = '';
      ({ data, error } = await runPage());
    }

    if (error) throw error;
    allRows.push(...(data || []));
    hasMore = (data?.length ?? 0) === PAGE_SIZE;
    from += PAGE_SIZE;
  }

  return allRows.map(d => dbLeadToDeal(d as any)) as Deal[];
};

/** Hook React Query para buscar todos os deals de um projeto (paginado) */
export function useDeals(projectId: string | null) {
  return useQuery({
    queryKey: queryKeys.deals(projectId),
    queryFn: () => projectId ? fetchDeals(projectId) : Promise.resolve([]),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5,
    // U-03: pontua os deals na FONTE (idempotente — score derivado dos campos,
    // não de `deal.score`). Assim Pipeline, Leads, Dashboard, Relatórios e
    // Highlights mostram o MESMO score (antes Pipeline calculava local = 20 e
    // os demais usavam o score cru do banco = 0).
    select: scoreAllDeals,
  });
}

